import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';

const industries = [
  {
    id: 'residential',
    title: 'Private Residences',
    description: 'Exterior maintenance programs designed for high-value residential properties where appearance, consistency, and property standards are critical.'
  },
  {
    id: 'hoa',
    title: 'HOAs & Multi-Family Communities',
    description: 'Comprehensive exterior maintenance programs for community associations and multi-family properties with consistent service and detailed reporting.'
  },
  {
    id: 'corporate',
    title: 'Corporate Campuses',
    description: 'Professional grounds maintenance programs designed to maintain property appearance, safety, and operational standards.'
  },
  {
    id: 'retail',
    title: 'Retail Centers',
    description: 'Scheduled exterior maintenance programs that maintain property appearance and support tenant and visitor experience.'
  }
];

// Hero Section
const IndustriesHero = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="industries-hero">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-white mb-4 uppercase">
        Industries We Serve
      </h1>
      <p className="text-lg text-slate-300 max-w-2xl mb-3">
        Exterior maintenance programs designed to meet the unique requirements and standards of each property type.
      </p>
      <p className="text-slate-400">
        Serving private estates, residential communities, and commercial properties throughout Los Angeles County.
      </p>
    </div>
  </section>
);

// Industries List
const IndustriesList = () => (
  <section className="bg-slate-50 py-16" data-testid="industries-list">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {industries.map((industry) => (
          <div 
            key={industry.id}
            className="bg-white p-8 border border-slate-200 border-t-4 border-t-[#1a2e44]"
          >
            <h3 className="text-xl font-bold text-[#1a2e44] mb-3">
              {industry.title}
            </h3>
            <p className="text-slate-600 text-sm mb-4">
              {industry.description}
            </p>
            <Link
              to={`/contact?intent=assessment&industry=${industry.id}&source=industries-${industry.id}`}
              className="inline-flex items-center text-sm font-bold text-[#1a2e44] hover:text-[#1a2e44]/70"
            >
              Learn more <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// CTA Section
const IndustriesCTA = () => (
  <section className="bg-[#1a2e44] py-16" data-testid="industries-cta">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-2xl font-bold text-white mb-4">
        Have Questions About Your Property Type?
      </h2>
      <p className="text-slate-400 mb-8 max-w-xl mx-auto">
        Contact us to discuss how we can develop a program for your specific requirements.
      </p>
      <Link
        to="/contact?intent=assessment"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="industries-cta-btn"
      >
        Request a Site Assessment
      </Link>
    </div>
  </section>
);

export default function IndustriesPage() {
  useEffect(() => {
    document.title = 'Industries We Serve | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="industries-page">
      <IndustriesHero />
      <IndustriesList />
      <IndustriesCTA />
    </div>
  );
}
